/**
 * ProcedureIntakePage
 *
 * A multi-step intake form dynamically built from a procedure's `intake_spec`
 * in gm-content.json. Visually matches the Spain Regularization 2026 applicant
 * portal (cream bg, white cards, emerald buttons, red-top header) so users get
 * a consistent experience across all 48 procedures.
 *
 * Steps:
 *   0  - Landing (procedure info + "Get Started")
 *   1‥N - One step per display_group that contains `kind:"info"` items
 *   N+1 - Documents checklist (all `kind:"document"` items)
 *   N+2 - Completion
 */

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProcedure } from '../../procedures/ProcedureContext';
import { pickEn } from '../../procedures/procedureContent';
import { ORIGIN_COUNTRIES } from '../../utils/countries';
import { mvpApi } from '../../services/api';
import ProcedureInfoPage from './ProcedureInfoPage';
import CrewParticipantWelcome from '../../components/participant/CrewParticipantWelcome';
import { getCrewParticipantContext, intakePrefillFromCrew } from '../../utils/participantCrewContext';
import { localizedPath } from '../../utils/postLoginNavigation';
import { useForm } from '../../applicant/context/FormContext';
import EngagementSection from '../../applicant/components/sections/EngagementSection';
import { isEngagementSignatureDataUrlValid } from '../../applicant/utils/signatureFieldValidation';

/* ─── Storage helpers ──────────────────────────────────────────────── */
const STORAGE_KEY = (slug) => `proc_intake_${slug}`;

function loadDraft(slug) {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY(slug)) || '{}');
  } catch {
    return {};
  }
}
function saveDraft(slug, values) {
  try {
    localStorage.setItem(STORAGE_KEY(slug), JSON.stringify(values));
  } catch { /* quota */ }
}

/* ─── PREFILL MAP: registration field → intake item id ────────────── */
const PREFILL_MAP = {
  first_name: 'in.app.given_name',
  middle_name: 'in.app.middle_name',
  last_name:   'in.app.surname1',
  surname2:    'in.app.surname2',
  dob:         'in.app.dob',
  nationality: 'in.app.nationality',
  email:       'in.app.email',
  phone:       'in.app.phone_no',
};

/* ─── Header ───────────────────────────────────────────────────────── */
function IntakeHeader({ name = '', onLogoClick }) {
  const [dropOpen, setDropOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [lang, setLang]         = useState('en');
  const dropRef = useRef(null);
  const langRef = useRef(null);

  const initials = name
    ? name.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()
    : '';

  const LANGS = [
    { value: 'en', label: 'English',  native: 'EN' },
    { value: 'es', label: 'Español',  native: 'ES' },
    { value: 'ar', label: 'العربية',   native: 'AR' },
    { value: 'fr', label: 'Français', native: 'FR' },
    { value: 'zh', label: '中文',      native: '中文' },
  ];

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    try { localStorage.clear(); } catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }
    window.location.assign('/login');
  };

  return (
    <header
      className="w-full sticky top-0 z-40 bg-white"
      style={{ borderTop: '2px solid #a81c1c', borderBottom: '1px solid #e2e8f0' }}
    >
      <div className="max-w-[780px] mx-auto px-5 h-14 flex items-center justify-between gap-4">

        {/* Logo */}
        <button
          type="button"
          onClick={onLogoClick}
          className="flex items-center min-w-0 cursor-pointer hover:opacity-80 transition-opacity h-full py-1"
        >
          <img src="/header-logo.png" alt="Global Mobility" className="h-10 w-auto object-contain" style={{ maxHeight: '40px' }} />
        </button>

        {/* Right controls */}
        <div className="flex items-center gap-3">

          {/* Language switcher */}
          <div className="relative" ref={langRef}>
            <button
              type="button"
              onClick={() => { setLangOpen((v) => !v); setDropOpen(false); }}
              className="flex items-center gap-1.5 h-7 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-translate-2 text-slate-400 text-[13px]" />
              <span className="text-[12px] font-bold text-slate-600 uppercase tracking-wide">
                {LANGS.find((l) => l.value === lang)?.native}
              </span>
              <i className={`ri-arrow-down-s-line text-slate-400 text-xs transition-transform ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 mt-2 bg-white border border-slate-100 rounded-xl overflow-hidden z-50 min-w-[150px]" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                {LANGS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => { setLang(opt.value); setLangOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap text-left ${
                      lang === opt.value ? 'bg-slate-50 text-slate-900 font-semibold' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {lang === opt.value
                      ? <i className="ri-check-line text-slate-500 text-xs shrink-0" />
                      : <span className="w-3.5 shrink-0" />}
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Profile / avatar dropdown */}
          <div className="relative" ref={dropRef}>
            <button
              type="button"
              onClick={() => { setDropOpen((v) => !v); setLangOpen(false); }}
              className="flex items-center gap-1 cursor-pointer group rounded-lg p-0.5 -mr-0.5"
              aria-expanded={dropOpen}
              aria-haspopup="menu"
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0"
                style={{ background: '#334155' }}
              >
                {initials}
              </div>
              <span className="hidden sm:block text-[13px] font-medium text-slate-700 truncate max-w-[120px] group-hover:text-slate-900 transition-colors">
                {name}
              </span>
              <i className={`ri-arrow-down-s-line text-slate-400 text-sm shrink-0 transition-transform ${dropOpen ? 'rotate-180' : ''}`} />
            </button>

            {dropOpen && (
              <div className="absolute top-full right-0 mt-2 bg-white border border-slate-100 rounded-xl overflow-hidden z-50 min-w-[220px]" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                {name && (
                  <>
                    <div className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900 whitespace-nowrap">{name}</p>
                    </div>
                    <div className="h-px bg-slate-100" />
                  </>
                )}
                <a
                  href="https://globalmobility.law/politica-privacidad"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setDropOpen(false)}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-shield-check-line text-slate-400 text-sm shrink-0" />
                  Privacy policy
                </a>
                <div className="h-px bg-slate-100" />
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 cursor-pointer transition-colors whitespace-nowrap text-left"
                >
                  <i className="ri-logout-box-r-line text-slate-400 text-sm shrink-0" />
                  Log out
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </header>
  );
}

/* ─── Progress bar ─────────────────────────────────────────────────── */
function StepProgressBar({ step, totalSteps, stepLabel }) {
  const pct = totalSteps > 0 ? Math.round((step / totalSteps) * 100) : 0;
  return (
    <div className="w-full bg-white border-b border-slate-100 px-4 py-3">
      <div className="max-w-[480px] mx-auto">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            {stepLabel}
          </span>
          <span className="text-xs text-slate-400">
            {step} / {totalSteps}
          </span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500 ease-out"
            style={{ width: `${pct}%`, background: '#059669' }}
          />
        </div>
      </div>
    </div>
  );
}

/* ─── Single form field ─────────────────────────────────────────────  */
function IntakeField({ item, value, onChange, disabled }) {
  const label = pickEn(item.prompt?.text);
  const isRequired = item.required === 'required';
  const id = `pi-${item.id}`;
  const inputClass =
    'w-full h-12 px-4 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 ' +
    'focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 transition-colors text-[15px]';

  const fieldLabel = (
    <label htmlFor={id} className="block text-[12px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
      {label}
      {isRequired && <span className="text-red-400 ml-0.5">*</span>}
      {item.required === 'optional' && (
        <span className="text-slate-300 font-normal ml-1 normal-case tracking-normal">(optional)</span>
      )}
    </label>
  );

  if (item.data_type === 'date') {
    return (
      <div className="py-2">
        {fieldLabel}
        <input
          id={id}
          type="date"
          className={inputClass}
          value={value || ''}
          onChange={(e) => onChange(item.id, e.target.value)}
          disabled={disabled}
          style={{ fontSize: '16px' }}
        />
      </div>
    );
  }

  if (item.data_type === 'country') {
    return (
      <div className="py-2">
        {fieldLabel}
        <select
          id={id}
          className={`${inputClass} cursor-pointer`}
          value={value || ''}
          onChange={(e) => onChange(item.id, e.target.value)}
          disabled={disabled}
          style={{ fontSize: '16px' }}
        >
          <option value="">Select country…</option>
          {ORIGIN_COUNTRIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
    );
  }

  if (item.data_type === 'enum' && Array.isArray(item.options)) {
    return (
      <div className="py-2">
        {fieldLabel}
        <div className="flex flex-col gap-2 mt-1">
          {item.options.map((opt) => {
            const v = opt.value ?? opt;
            const lbl = pickEn(opt.label) || v;
            return (
              <button
                key={v}
                type="button"
                disabled={disabled}
                onClick={() => onChange(item.id, v)}
                className={`w-full min-h-[44px] px-4 rounded-xl border text-left text-[14px] transition-all cursor-pointer whitespace-normal ${
                  value === v
                    ? 'border-emerald-400 bg-emerald-50 text-emerald-800 font-semibold'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50/50'
                }`}
              >
                {lbl}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (item.data_type === 'bool') {
    return (
      <div className="py-2">
        {fieldLabel}
        <div className="flex gap-3 mt-1">
          {['yes', 'no'].map((v) => (
            <button
              key={v}
              type="button"
              disabled={disabled}
              onClick={() => onChange(item.id, v)}
              className={`flex-1 h-12 rounded-xl border text-[14px] font-semibold transition-all cursor-pointer ${
                value === v
                  ? 'border-emerald-400 bg-emerald-50 text-emerald-800'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-300'
              }`}
            >
              {v === 'yes' ? 'Yes' : 'No'}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (item.data_type === 'email') {
    return (
      <div className="py-2">
        {fieldLabel}
        <input
          id={id}
          type="email"
          className={inputClass}
          value={value || ''}
          onChange={(e) => onChange(item.id, e.target.value)}
          disabled={disabled}
          autoComplete="email"
          style={{ fontSize: '16px' }}
        />
      </div>
    );
  }

  return (
    <div className="py-2">
      {fieldLabel}
      <input
        id={id}
        type="text"
        className={inputClass}
        value={value || ''}
        onChange={(e) => onChange(item.id, e.target.value)}
        disabled={disabled}
        style={{ fontSize: '16px' }}
      />
    </div>
  );
}

/* ─── Map intake item id → document_type for upload API ────────────  */
function itemIdToDocType(itemId = '') {
  // Strip prefix e.g. "in.app.doc_passport" → "passport"
  const raw = itemId.replace(/^in\.(app|emp|gov)\.doc_?/, '').replace(/^doc_?/, '');
  const map = {
    passport: 'passport',
    visa: 'residence',
    contract: 'employment',
    payslip: 'employment',
    social_security: 'employment',
    criminal_record: 'criminal',
    birth_cert: 'other',
    marriage_cert: 'other',
    photo: 'other',
  };
  // Match any key that appears in the raw string
  for (const [k, v] of Object.entries(map)) {
    if (raw.includes(k)) return v;
  }
  return 'other';
}

/* ─── Single document upload card ──────────────────────────────────  */
function DocUploadCard({ item, uploadedFilename, onUpload, sessionId, onSessionNeeded }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const label = pickEn(item.prompt?.text);
  const isRequired = item.required === 'required';
  const isUploaded = Boolean(uploadedFilename);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      let sid = sessionId;
      if (!sid) sid = await onSessionNeeded();
      if (!sid) throw new Error('Could not create upload session');
      const docType = itemIdToDocType(item.id);
      await mvpApi.uploadDocument(sid, file, docType);
      onUpload(item.id, file.name);
    } catch (err) {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleClear = () => {
    onUpload(item.id, '');
    if (fileRef.current) fileRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  };

  return (
    <div className={`bg-white rounded-2xl border transition-all ${isUploaded ? 'border-emerald-300' : 'border-slate-200'}`}>
      <div className="px-4 pt-4 pb-3">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isUploaded ? 'bg-emerald-100' : 'bg-slate-100'}`}>
            <i className={`text-base ${isUploaded ? 'ri-file-check-line text-emerald-600' : 'ri-file-line text-slate-400'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-slate-800 leading-snug">{label}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {isRequired ? 'Required' : 'Optional'}
            </p>
          </div>
          {isUploaded && (
            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-check-line text-white text-[10px]" />
            </div>
          )}
        </div>

        {/* Uploaded state */}
        {isUploaded ? (
          <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <i className="ri-attachment-line text-emerald-600 text-sm shrink-0" />
              <span className="text-[12px] text-emerald-700 truncate font-medium">{uploadedFilename}</span>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="text-[11px] text-slate-400 hover:text-red-500 transition-colors ml-2 shrink-0 cursor-pointer"
            >
              Remove
            </button>
          </div>
        ) : (
          /* Upload zone */
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-4">
            {uploading ? (
              <div className="flex items-center justify-center gap-2 py-1">
                <i className="ri-loader-4-line animate-spin text-slate-400" />
                <span className="text-[13px] text-slate-400">Uploading…</span>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex-1 h-10 flex items-center justify-center gap-1.5 bg-white border border-slate-200 rounded-xl text-[13px] font-medium text-slate-700 cursor-pointer hover:border-emerald-400 hover:text-emerald-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-file-upload-line text-sm" />
                  Upload file
                </button>
                <button
                  type="button"
                  onClick={() => cameraRef.current?.click()}
                  className="h-10 px-3 flex items-center justify-center gap-1.5 bg-white border border-slate-200 rounded-xl text-[13px] font-medium text-slate-700 cursor-pointer hover:border-emerald-400 hover:text-emerald-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-camera-line text-sm" />
                  Camera
                </button>
              </div>
            )}
            {error && (
              <p className="text-[11px] text-red-500 mt-2 text-center">{error}</p>
            )}
          </div>
        )}
      </div>

      {/* Hidden inputs */}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,application/pdf" onChange={handleInputChange} className="hidden" />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleInputChange} className="hidden" />
    </div>
  );
}

/* ─── Document upload section ───────────────────────────────────────  */
function DocUploadSection({ docItems, values, onUpload, sessionId, onSessionNeeded }) {
  return (
    <div className="max-w-[480px] mx-auto px-4 pb-24 sm:pb-10">
      <div className="mb-5">
        <h2 className="text-[18px] font-bold text-slate-900 mb-1">
          Upload your documents
        </h2>
        <p className="text-[13px] text-slate-500 leading-relaxed">
          Upload each required document. You can take a photo with your camera or
          select a file from your device. JPEG, PNG and PDF are accepted.
        </p>
      </div>
      <div className="flex flex-col gap-3 mb-6">
        {docItems.map((item) => (
          <DocUploadCard
            key={item.id}
            item={item}
            uploadedFilename={values[item.id] || ''}
            onUpload={onUpload}
            sessionId={sessionId}
            onSessionNeeded={onSessionNeeded}
          />
        ))}
      </div>
    </div>
  );
}

/* LandingScreen is replaced by the imported ProcedureInfoPage */

/* ─── Completion screen ─────────────────────────────────────────────  */
function CompletionScreen({ procedureTitle, navigate }) {
  return (
    <div className="min-h-[calc(100vh-56px)] bg-[#F8F7F4] flex flex-col">
      <div className="flex-1 flex items-center justify-center py-8">
        <div className="max-w-[420px] mx-auto px-4 text-center">

          <div
            className="w-16 h-16 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center mx-auto mb-5"
          >
            <i className="ri-check-double-line text-emerald-600 text-2xl" />
          </div>

          <h1 className="text-[22px] font-bold text-slate-900 mb-3">
            Information submitted!
          </h1>
          <p className="text-[14px] text-slate-500 leading-relaxed mb-6">
            Your details and document checklist for{' '}
            <span className="text-slate-700 font-semibold">{procedureTitle}</span>{' '}
            have been saved. Your advisor will review everything and be in touch to guide
            the next steps.
          </p>

          <button
            type="button"
            onClick={() => navigate(localizedPath('/organizer'), { replace: true })}
            className="w-full h-12 flex items-center justify-center gap-2 rounded-xl font-semibold text-[15px] cursor-pointer transition-colors whitespace-nowrap bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm mb-3"
          >
            View my case
          </button>

          <button
            type="button"
            onClick={() => navigate(localizedPath('/organizer'), { replace: true })}
            className="w-full h-12 flex items-center justify-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl font-medium text-[14px] cursor-pointer transition-colors whitespace-nowrap"
          >
            Upload documents
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Main component
   ════════════════════════════════════════════════════════════════════ */
export default function ProcedureIntakePage() {
  const { slug, bundle, loading, error, procedure } = useProcedure();
  const navigate = useNavigate();
  const location = useLocation();

  /* ── Parse intake spec ── */
  const intakeItems = useMemo(
    () => (bundle?.intake?.items || []).filter((i) => i.collect !== false),
    [bundle]
  );
  const displayGroupsMeta = bundle?.intake?.display_groups || {};

  const infoItems = useMemo(() => intakeItems.filter((i) => i.kind === 'info'), [intakeItems]);
  const docItems  = useMemo(() => intakeItems.filter((i) => i.kind === 'document'), [intakeItems]);

  /** Ordered list of unique display_groups that hold info items */
  const infoGroups = useMemo(() => {
    const seen = new Set();
    const order = [];
    infoItems.forEach((item) => {
      const g = item.display_group || '__other__';
      if (!seen.has(g)) { seen.add(g); order.push(g); }
    });
    return order.map((g) => ({
      groupId: g,
      label: pickEn(displayGroupsMeta[g]?.text) || g,
      items: infoItems.filter((i) => (i.display_group || '__other__') === g),
    }));
  }, [infoItems, displayGroupsMeta]);

  /* Steps: 0=landing, 1..N=info groups, N+1=docs (optional), N+2=sign, N+3=completion */
  const hasDocs = docItems.length > 0;
  const SIGN_STEP = infoGroups.length + (hasDocs ? 1 : 0) + 1;
  const TOTAL_STEPS = SIGN_STEP;

  const [step, setStep]       = useState(0);   // 0 = landing; synced from nav state once intake is ready
  const [values, setValues]   = useState(() => loadDraft(slug));
  const [saving, setSaving]   = useState(false);
  const [stepError, setStepError] = useState('');
  const [autoSave, setAutoSave] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [sessionId, setSessionId] = useState(null);
  const [userName, setUserName]   = useState('');
  const { data: formData } = useForm();
  const crewContext = useMemo(() => getCrewParticipantContext(), []);
  const navIntakeStep = Number(location.state?.intakeStep);
  const hasNavIntakeStep = Number.isFinite(navIntakeStep) && navIntakeStep > 0;
  // Crew invitees see a welcome/confirm screen first; "Continue" reveals the full
  // procedure content (which has its own "Start application" CTA into the wizard).
  // Deep-link from Organizer skips welcome and goes straight into the form.
  const [welcomeAcknowledged, setWelcomeAcknowledged] = useState(
    !crewContext || hasNavIntakeStep,
  );

  useEffect(() => {
    if (!bundle || !hasNavIntakeStep) return;
    const capped = Math.min(Math.max(0, navIntakeStep), TOTAL_STEPS + 1);
    setStep(capped);
    if (capped > 0) setWelcomeAcknowledged(true);
  }, [bundle, hasNavIntakeStep, navIntakeStep, TOTAL_STEPS]);

  const savedRef = useRef(false);
  const autoSaveTimer = useRef(null);
  const mongoSaveTimer = useRef(null);
  const skipMongoSaveRef = useRef(false);

  /* ── Prefill from registration via getMe + crew import + Mongo autosave ── */
  useEffect(() => {
    if (!slug) return;

    const crewPatch = intakePrefillFromCrew(crewContext);
    if (Object.keys(crewPatch).length > 0) {
      setValues((prev) => {
        const patch = {};
        Object.entries(crewPatch).forEach(([itemId, val]) => {
          if (val && !prev[itemId]) patch[itemId] = val;
        });
        return Object.keys(patch).length > 0 ? { ...prev, ...patch } : prev;
      });
    }

    const token = localStorage.getItem('authToken');
    const loaders = [];

    if (token) {
      loaders.push(
        mvpApi.getProcedureIntake(slug)
          .then((res) => {
            const remote = res.data?.values || {};
            if (!remote || typeof remote !== 'object') return;
            skipMongoSaveRef.current = true;
            setValues((prev) => {
              const patch = {};
              Object.entries(remote).forEach(([itemId, val]) => {
                if (val != null && val !== '') patch[itemId] = val;
              });
              const next = Object.keys(patch).length > 0 ? { ...prev, ...patch } : prev;
              saveDraft(slug, next);
              return next;
            });
            setTimeout(() => { skipMongoSaveRef.current = false; }, 0);
          })
          .catch(() => { /* not logged in or no saved intake yet */ })
      );
    }

    loaders.push(
      mvpApi.getMe().then((res) => {
        const user = res.data?.user || res.data;
        if (!user) return;
        const full = [
          user.first_name || user.firstName || '',
          user.last_name  || user.lastName  || '',
        ].filter(Boolean).join(' ').trim() || user.full_name || user.fullName || '';
        if (full) setUserName(full);
        setValues((prev) => {
          const patch = {};
          Object.entries(PREFILL_MAP).forEach(([apiField, itemId]) => {
            if (user[apiField] && !prev[itemId]) {
              patch[itemId] = user[apiField];
            }
          });
          return Object.keys(patch).length > 0 ? { ...prev, ...patch } : prev;
        });
      }).catch(() => { /* user not logged in yet - fine */ })
    );

    Promise.allSettled(loaders);
  }, [slug, crewContext]);

  /* ── Auto-save draft on value change (localStorage + Mongo) ── */
  useEffect(() => {
    if (!savedRef.current) { savedRef.current = true; return; }
    setAutoSave('saving');
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveDraft(slug, values);
      setAutoSave('saved');
      setTimeout(() => setAutoSave('idle'), 2000);
    }, 800);

    const token = localStorage.getItem('authToken');
    if (token && !skipMongoSaveRef.current) {
      clearTimeout(mongoSaveTimer.current);
      mongoSaveTimer.current = setTimeout(() => {
        mvpApi.autosaveProcedureIntake(slug, values).catch(() => {
          /* best-effort; local draft remains */
        });
      }, 1200);
    }
  }, [slug, values]);

  const handleChange = useCallback((id, val) => {
    setValues((prev) => ({ ...prev, [id]: val }));
  }, []);

  /** Lazily creates / returns a case upload session. */
  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    try {
      const res = await mvpApi.startSession({ language: 'en' });
      const sid = String(res?.data?.session_id ?? res?.data?.sessionId ?? '').trim();
      if (sid) { setSessionId(sid); return sid; }
    } catch { /* caller shows error */ }
    return null;
  }, [sessionId]);

  /** Called by DocUploadCard on successful upload */
  const handleDocUpload = useCallback((id, filename) => {
    setValues((prev) => {
      const next = { ...prev, [id]: filename };
      saveDraft(slug, next);
      return next;
    });
  }, [slug]);

  /* ── Validation ── */
  const requiredInfoIds = useMemo(
    () => infoItems.filter((i) => i.required === 'required').map((i) => i.id),
    [infoItems]
  );
  const requiredDocIds = useMemo(
    () => docItems.filter((i) => i.required === 'required').map((i) => i.id),
    [docItems]
  );

  /** For the current info step (1-indexed), check whether all required items in that group are filled */
  const currentGroupValid = useMemo(() => {
    if (step === 0 || step > infoGroups.length) return true;
    const group = infoGroups[step - 1];
    return group.items
      .filter((i) => i.required === 'required')
      .every((i) => Boolean((values[i.id] || '').toString().trim()));
  }, [step, infoGroups, values]);

  const allInfoFilled = requiredInfoIds.every(
    (id) => Boolean((values[id] || '').toString().trim())
  );
  // A doc is "done" when its value is a non-empty string (uploaded filename)
  const allDocsChecked = requiredDocIds.every(
    (id) => typeof values[id] === 'string' && values[id].trim() !== ''
  );

  const isDocStep    = hasDocs && step === infoGroups.length + 1;
  const isSignStep   = step === SIGN_STEP;
  const isCompletion = step === TOTAL_STEPS + 1;

  const signStepValid = !!(
    formData.engagementConsent &&
    formData.dataProcessingConsent &&
    isEngagementSignatureDataUrlValid(formData.signature)
  );

  const canContinue = isSignStep
    ? signStepValid
    : isDocStep
      ? allDocsChecked
      : currentGroupValid;

  /* ── Navigation ── */
  const goNext = useCallback(async () => {
    setStepError('');
    if (isSignStep) {
      setSaving(true);
      try {
        await mvpApi.updateEngagementDetails({
          no_return_commitment: false,
          no_healthcare_coverage: false,
          engagement_consent: formData.engagementConsent === true,
          data_processing_consent: formData.dataProcessingConsent === true,
          marketing_consent: false,
          questions_requested: formData.questionsRequested === true,
          signature_data_url: formData.signature,
        });
        setStep((s) => s + 1);
        window.scrollTo(0, 0);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setStepError(typeof detail === 'string' ? detail : 'Could not save your signature. Please try again.');
      } finally {
        setSaving(false);
      }
      return;
    }
    if (isDocStep) {
      setSaving(true);
      saveDraft(slug, values);
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          await mvpApi.autosaveProcedureIntake(slug, values);
        } catch {
          /* local draft already saved */
        }
      }
      setSaving(false);
      setStep((s) => s + 1);
      window.scrollTo(0, 0);
      return;
    }
    setStep((s) => s + 1);
    window.scrollTo(0, 0);
  }, [isDocStep, isSignStep, slug, values, formData]);

  const goBack = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo(0, 0);
  }, []);

  /* ── Loading / error guards ── */
  if (loading) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-[#F8F7F4] flex flex-col items-center justify-center">
        <p className="text-slate-500 text-sm">Loading your form…</p>
      </div>
    );
  }

  if (error || !bundle || !bundle.intake) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-[#F8F7F4] flex flex-col items-center justify-center px-4 text-center">
        <i className="ri-error-warning-line text-4xl text-red-400 mb-3" />
        <p className="text-slate-600 text-sm">Unable to load the intake form. Please try again.</p>
      </div>
    );
  }

  const procedureTitle = pickEn(procedure?.title);

  /* ── Landing: crew welcome/confirm screen, then the full procedure info page ── */
  if (step === 0) {
    if (crewContext && !welcomeAcknowledged) {
      return (
        <div className="min-h-screen overflow-x-hidden bg-[#F8F7F4]">
          <IntakeHeader
            name={userName}
            onLogoClick={() => window.history.back()}
          />
          <div className="max-w-[780px] mx-auto px-5 pt-6 pb-24 sm:pb-10">
            <CrewParticipantWelcome context={crewContext} />
            <button
              type="button"
              onClick={() => { setWelcomeAcknowledged(true); window.scrollTo(0, 0); }}
              className="w-full h-12 flex items-center justify-center gap-2 text-[15px] font-semibold rounded-xl transition-colors whitespace-nowrap bg-emerald-600 text-white cursor-pointer hover:bg-emerald-700"
            >
              Continue
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen overflow-x-hidden">
        <IntakeHeader
          name={userName}
          onLogoClick={() => window.history.back()}
        />
        <ProcedureInfoPage
          procedure={procedure}
          bundle={bundle}
          intakeItems={intakeItems}
          onStart={() => setStep(1)}
        />
      </div>
    );
  }

  /* ── Completion ── */
  if (isCompletion) {
    return (
      <div className="min-h-screen overflow-x-hidden">
        <IntakeHeader name={userName} onLogoClick={() => navigate(localizedPath('/organizer'), { replace: true })} />
        <CompletionScreen procedureTitle={procedureTitle} navigate={navigate} />
      </div>
    );
  }

  /* ── Active step label & index ── */
  const isInfoStep = step >= 1 && step <= infoGroups.length;
  const currentGroup = isInfoStep ? infoGroups[step - 1] : null;
  const stepLabel = isSignStep
    ? 'Sign & submit'
    : isDocStep
      ? 'Documents checklist'
      : currentGroup?.label || `Step ${step}`;

  /* ── Render ── */
  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F8F7F4]">
      {/* Header */}
      <IntakeHeader
        name={userName}
        onLogoClick={() => setStep(0)}
      />

      {/* Auto-save indicator */}
      {autoSave !== 'idle' && (
        <div className="fixed top-14 right-4 z-30 px-3 py-1.5 rounded-full bg-white border border-slate-100 shadow-sm text-[11px] font-medium transition-all">
          {autoSave === 'saving' ? (
            <span className="text-slate-400 flex items-center gap-1">
              <i className="ri-loader-4-line animate-spin text-xs" /> Saving…
            </span>
          ) : (
            <span className="text-emerald-600 flex items-center gap-1">
              <i className="ri-checkbox-circle-line text-xs" /> Saved
            </span>
          )}
        </div>
      )}

      {/* Progress bar */}
      <StepProgressBar
        step={step}
        totalSteps={TOTAL_STEPS}
        stepLabel={stepLabel}
      />

      {/* ── Document upload step ── */}
      {isDocStep && (
        <>
          <div className="min-h-[calc(100vh-56px-56px)] bg-[#F8F7F4] pt-6">
            <DocUploadSection
              docItems={docItems}
              values={values}
              onUpload={handleDocUpload}
              sessionId={sessionId}
              onSessionNeeded={ensureSession}
            />
          </div>

          {/* Navigation */}
          <div className="max-w-[480px] mx-auto px-4 pb-24 sm:pb-10 pt-2">
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={goNext}
                disabled={!canContinue || saving}
                className="w-full h-12 flex items-center justify-center gap-2 text-[15px] font-semibold rounded-xl transition-colors whitespace-nowrap disabled:cursor-not-allowed bg-emerald-600 text-white cursor-pointer hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500"
              >
                {saving
                  ? <><i className="ri-loader-4-line animate-spin" /> Saving…</>
                  : 'Continue'}
              </button>
              <button
                type="button"
                onClick={goBack}
                className="w-full h-12 flex items-center justify-center gap-2 text-slate-600 text-[15px] font-medium rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors whitespace-nowrap"
              >
                Back
              </button>
            </div>
            {!canContinue && (
              <p className="text-[11px] text-slate-400 text-center mt-3">
                Upload all required documents to continue.
              </p>
            )}
          </div>
        </>
      )}

      {/* ── Sign & submit (engagement + signature → S3 + Mongo) ── */}
      {isSignStep && (
        <>
          <EngagementSection variant="procedure" />
          <div className="max-w-[480px] mx-auto px-4 pb-24 sm:pb-10 pt-2">
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={goNext}
                disabled={!canContinue || saving}
                className="w-full h-12 flex items-center justify-center gap-2 text-[15px] font-semibold rounded-xl transition-colors whitespace-nowrap disabled:cursor-not-allowed bg-emerald-600 text-white cursor-pointer hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500"
              >
                {saving
                  ? <><i className="ri-loader-4-line animate-spin" /> Saving signature…</>
                  : 'Sign and continue'}
              </button>
              <button
                type="button"
                onClick={goBack}
                className="w-full h-12 flex items-center justify-center gap-2 text-slate-600 text-[15px] font-medium rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors whitespace-nowrap"
              >
                Back
              </button>
            </div>
            {stepError ? (
              <p className="text-[12px] text-rose-600 text-center mt-3" role="alert">
                {stepError}
              </p>
            ) : null}
          </div>
        </>
      )}

      {/* ── Info section step ── */}
      {isInfoStep && currentGroup && (
        <div className="pt-6 pb-24 sm:pb-10">
          <div className="max-w-[480px] mx-auto px-4">

            {/* Section heading */}
            <div className="mb-4">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">
                {procedureTitle}
              </p>
              <h2 className="text-[20px] font-bold text-slate-900">{currentGroup.label}</h2>
            </div>

            {/* Fields card */}
            <div className="bg-white rounded-2xl border border-slate-200 px-5 py-3 mb-5 divide-y divide-slate-50">
              {currentGroup.items.map((item) => (
                <IntakeField
                  key={item.id}
                  item={item}
                  value={values[item.id]}
                  onChange={handleChange}
                />
              ))}
            </div>

            {/* Navigation */}
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={goNext}
                disabled={!currentGroupValid}
                className="w-full h-12 flex items-center justify-center gap-2 text-[15px] font-semibold rounded-xl transition-colors whitespace-nowrap disabled:cursor-not-allowed bg-emerald-600 text-white cursor-pointer hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500"
              >
                Continue
                <i className="ri-arrow-right-line" />
              </button>

              {step > 1 && (
                <button
                  type="button"
                  onClick={goBack}
                  className="w-full h-12 flex items-center justify-center gap-2 text-slate-600 text-[15px] font-medium rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors whitespace-nowrap"
                >
                  Back
                </button>
              )}
            </div>

            {!currentGroupValid && (
              <p className="text-[11px] text-slate-400 text-center mt-3">
                Fill in all required fields marked with * to continue.
              </p>
            )}

          </div>
        </div>
      )}
    </div>
  );
}
